import React, { createContext, useContext, useState, useEffect } from "react";
import { authService, SendSignInLinkResponse, auth } from "../services/authService";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8082' : 'https://api.rebuild.work');

interface LoginWithLinkResult {
  success: boolean;
  error?: string;
}

interface User {
  uid: string;
  email: string | null;
  organizationId?: string;
  lassoUserId?: string;
  role?: string;
  isRegistered?: boolean;
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  token: string | null;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  sendSignInLink: (email: string) => Promise<SendSignInLinkResponse>;
  loginWithCode: (code: string) => Promise<LoginWithLinkResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    // Set up auth state listener using Firebase JS SDK
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          const firebaseToken = await firebaseUser.getIdToken();
          setToken(firebaseToken);

          // Fetch user details from backend
          const response = await fetch(
            `${API_BASE_URL}/rest/v2/user`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "authorization": `Bearer ${firebaseToken}`,
              },
            }
          );

          const data = await response.json();

          if (data.success && data.data) {
            setCurrentUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              ...data.data,
            });
          } else {
            console.error("Failed to get user details:", data.error);
            setCurrentUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
            });
          }
        } catch (error) {
          console.error("Failed to get user data:", error);
          setToken(null);
          setCurrentUser(null);
        }
      } else {
        setToken(null);
        setCurrentUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const logout = async () => {
    await authService.logout();
    setToken(null);
    setCurrentUser(null);
  };

  const sendSignInLink = async (email: string) => {
    return await authService.sendSignInLink(email);
  };

  const loginWithCode = async (code: string) => {
    const result = await authService.loginWithCode(code);
    // No need to manually update token - the auth state listener will handle it
    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        token,
        isAuthenticated: !!token,
        logout,
        sendSignInLink,
        loginWithCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
